import { applyPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { CaseStudy, serviceCaseStudy } from "@/components/site/CaseStudy";
import { TrustSignals } from "@/components/site/TrustSignals";
import { FAQSection } from "@/components/site/FAQSection";
import { HeroLeadForm } from "@/components/site/HeroLeadForm";
import RebuildOrRefreshCheck from "@/components/blog/RebuildOrRefreshCheck";
import { Check } from "lucide-react";
import { services } from "@/data/services";
import { jsonLdScript, ogImageFor } from "@/lib/seo";
const TITLE = "Law Firm Website Development & Design | PageOne Lawyers";
const DESCRIPTION =
  "Law firm website development and design that loads in under 2 seconds on mobile, meets WCAG 2.2 AA and launches in 2 to 4 weeks. You own everything.";
const URL = "https://pageonelawyers.com/services/law-firm-website-development";
const OG_IMAGE = ogImageFor("service", "web-design");
const SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://pageonelawyers.com/services/law-firm-website-development#webpage",
    url: "https://pageonelawyers.com/services/law-firm-website-development",
    name: "Law Firm Website Development & Design | PageOne Lawyers",
    description:
      "Law firm website development and design that loads in under 2 seconds on mobile, meets WCAG 2.2 AA and launches in 2 to 4 weeks. You own everything.",
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "PageOne Lawyers",
      url: "https://pageonelawyers.com/",
    },
    breadcrumb: {
      "@id": "https://pageonelawyers.com/services/law-firm-website-development#breadcrumb",
    },
    mainEntity: {
      "@id": "https://pageonelawyers.com/services/law-firm-website-development#service",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://pageonelawyers.com/services/law-firm-website-development#service",
    name: "Law Firm Website Development and Design",
    serviceType: "Website development",
    description:
      "Website development and design for law firms on any CMS: custom design, mobile pages under 2 seconds, WCAG 2.2 AA accessibility, intake forms and call tracking, SEO built in, migration without losing rankings, and optional maintenance. Typical launch in 2 to 4 weeks.",
    url: "https://pageonelawyers.com/services/law-firm-website-development",
    provider: {
      "@type": "Organization",
      name: "PageOne Lawyers",
      url: "https://pageonelawyers.com/",
    },
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Law firms and attorneys",
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pageonelawyers.com/services/law-firm-website-development#breadcrumb",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://pageonelawyers.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://pageonelawyers.com/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Law Firm Website Development",
        item: "https://pageonelawyers.com/services/law-firm-website-development",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://pageonelawyers.com/services/law-firm-website-development#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does law firm website development cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Every project is quoted separately, because a five-page site for a solo practice and a fifty-page site for a multi-office firm are very different builds. The quote depends on page count, the CMS, integrations and whether we're migrating an existing site. You'll have a fixed quote before any work starts.",
        },
      },
      {
        "@type": "Question",
        name: "How long does it take to build a law firm website?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most builds launch in 2 to 4 weeks. Larger sites, or projects waiting on content and approvals, can take a little longer.",
        },
      },
      {
        "@type": "Question",
        name: "Which CMS do you build on?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We work with all major CMS platforms, including WordPress, Webflow and headless setups, and we can build on the CMS your team already knows. We'll recommend the one that fits how you want to manage the site.",
        },
      },
      {
        "@type": "Question",
        name: "Will we own the website?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The firm owns the website, the domain, the hosting account and all files and content. Nothing is locked to us.",
        },
      },
      {
        "@type": "Question",
        name: "Will a redesign hurt our Google rankings?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "It can if migration is handled badly. We inventory every existing URL, redirect each one to the right new page, carry over the content and metadata that rank, and monitor Search Console after launch.",
        },
      },
      {
        "@type": "Question",
        name: "Can you write the content for the new site?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Our legal content writers can write or refresh your practice pages, attorney bios and FAQs, written for clients rather than other lawyers.",
        },
      },
      {
        "@type": "Question",
        name: "Will the site be ADA accessible?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every site we build meets WCAG 2.2 AA, and we test with keyboard navigation and screen readers before launch.",
        },
      },
      {
        "@type": "Question",
        name: "Do you offer maintenance after launch?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Our optional maintenance plans cover updates, security monitoring, backups and small content changes, so your site stays fast and secure.",
        },
      },
      {
        "@type": "Question",
        name: "Is law firm mobile website development included?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every page is built mobile-first, loads in under 2 seconds on a phone and passes Google's Core Web Vitals.",
        },
      },
    ],
  },
];
const FAQS = [
  {
    q: "How much does law firm website development cost?",
    a: "Every project is quoted separately, because a five-page site for a solo practice and a fifty-page site for a multi-office firm are very different builds. The quote depends on page count, the CMS, integrations and whether we're migrating an existing site. You'll have a fixed quote before any work starts.",
  },
  {
    q: "How long does it take to build a law firm website?",
    a: "Most builds launch in 2 to 4 weeks. Larger sites, or projects waiting on content and approvals, can take a little longer.",
  },
  {
    q: "Which CMS do you build on?",
    a: "We work with all major CMS platforms, including WordPress, Webflow and headless setups, and we can build on the CMS your team already knows. We'll recommend the one that fits how you want to manage the site.",
  },
  {
    q: "Will we own the website?",
    a: "Yes. The firm owns the website, the domain, the hosting account and all files and content. Nothing is locked to us.",
  },
  {
    q: "Will a redesign hurt our Google rankings?",
    a: "It can if migration is handled badly. We inventory every existing URL, redirect each one to the right new page, carry over the content and metadata that rank, and monitor Search Console after launch.",
  },
  {
    q: "Can you write the content for the new site?",
    a: "Yes. Our legal content writers can write or refresh your practice pages, attorney bios and FAQs, written for clients rather than other lawyers.",
  },
  {
    q: "Will the site be ADA accessible?",
    a: "Yes. Every site we build meets WCAG 2.2 AA, and we test with keyboard navigation and screen readers before launch.",
  },
  {
    q: "Do you offer maintenance after launch?",
    a: "Yes. Our optional maintenance plans cover updates, security monitoring, backups and small content changes, so your site stays fast and secure.",
  },
  {
    q: "Is law firm mobile website development included?",
    a: "Yes. Every page is built mobile-first, loads in under 2 seconds on a phone and passes Google's Core Web Vitals.",
  },
];
export const Route = createFileRoute("/services/law-firm-website-development")({
  head: () => applyPageMetadata("/services/law-firm-website-development", {
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Web Design for Law Firms — PageOne Lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: SCHEMAS.map(jsonLdScript),
  }),
  component: WebsiteDevelopmentPage,
});
function WebsiteDevelopmentPage() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-primary-foreground/70">
            <ol className="flex flex-wrap gap-1">
              <li>
                <Link to="/">Home</Link> /
              </li>
              <li>
                <Link to="/services">Services</Link> /
              </li>
              <li>Law Firm Website Development</li>
            </ol>
          </nav>
          <Eyebrow>
            <span className="text-gold">Service</span>
          </Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
            {"Law Firm Website Development and Design"}
          </h1><LastUpdated />
          <p className="mt-6 max-w-3xl text-xl text-primary-foreground/85">
            {
              "Fast, accessible websites built to rank, earn trust and turn visitors into consultations. Live in 2 to 4 weeks."
            }
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get a free website review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Talk to a strategist
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <p className="max-w-4xl text-lg leading-relaxed">
          {
            "Law firm website development is about more than a good-looking homepage. Your site has to load quickly on a phone, answer the questions a worried client is asking, make calling you effortless, and give Google a clear picture of who you are and where you practice. We provide website development for law firms on the CMS that suits you, with SEO, intake and tracking built in from the first page, and you own everything we build."
          }
        </p>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"What's Included in Our Law Firm Website Development"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Every attorney web development project we take on covers the same essentials, whether you are a solo practice launching your first site or a multi-office firm replacing an old one."
            }
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Strategy and site plan"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "Before any design work, we map the pages your firm needs: one for each practice area and case type you want, office pages for real locations, attorney bios and the questions clients ask before they call. On a redesign, we inventory every existing page first so nothing that ranks gets lost."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Custom design that fits your firm"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "No recycled templates. Good legal website design and development go together, so the design is planned around the content and the build from day one. It reflects how your firm actually works and who it serves, with clear calls to action on every page and real photos of your attorneys rather than stock images."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Development on the CMS that suits you"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "We build on WordPress, Webflow, a headless setup or the CMS your team already uses. Legal website development shouldn't leave you dependent on us: what matters is that your staff can update pages and add new ones without calling a developer."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Fast on mobile"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "Most people looking for a lawyer search on their phone, often in a hurry. We build every page to load in under 2 seconds on mobile and to pass Google's Core Web Vitals, and we test on real devices before launch."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Accessible to every visitor"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "Every site we build meets WCAG 2.2 AA: readable contrast, keyboard navigation, proper headings, labelled forms and descriptive alt text. It's the right thing for clients with disabilities, and it reduces the risk of accessibility complaints."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Intake forms and call tracking"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "Short forms that ask only what intake needs, tap-to-call on every page, and tracking that shows which pages bring calls, form submissions and chats. You can see what the site is producing from the first week."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"SEO built in from the first page"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "Clean URLs, a logical page structure, title tags and meta descriptions written for each page, internal links between related practice areas, and structured data such as LegalService for your firm and Person for each attorney."
                }
              </p>
            </article>
            <article className="rounded-lg border border-border bg-background p-6">
              <h3 className="text-xl font-semibold">{"Training, handover and maintenance"}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {
                  "You own the site, the domain, the hosting account and every file. We show your team how to update it, and if you'd rather not manage it yourselves, our maintenance plans cover updates, security, backups and small changes."
                }
              </p>
            </article>
          </div>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"How We Build a Law Firm Website in 2 to 4 Weeks"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Our law firm website design and development process is built for speed without cutting corners. Fast law firm website development comes from a clear plan and quick decisions, not from skipping testing. Most builds launch in 2 to 4 weeks. The range depends mainly on the number of pages and how quickly content and approvals come back from your side."
          }
        </p>
        <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
          <img
            src="/images/services/law-firm-website-development/website-build-timeline.svg"
            alt="A five-stage timeline: discovery and site plan, design, development and content, testing and launch, then maintenance after launch."
            width="1200"
            height="520"
            loading="lazy"
            decoding="async"
            className="h-auto w-full"
          />
          <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
            {"A typical build moves from plan to launch in 2 to 4 weeks, then into maintenance."}
          </figcaption>
        </figure>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Discovery and site plan"}</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              {
                "We learn which cases you want more of, review your current site and competitors, and agree on a page plan and the main paths a visitor takes to contact you."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Design"}</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              {
                "You see the key page designs on desktop and mobile and give feedback before anything is built."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Development and content"}</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              {
                "We build the site, add your content, set up forms, tap-to-call and tracking, and write titles, descriptions and structured data for every page."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"Testing and launch"}</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              {
                "We test speed, accessibility, forms and tracking on real devices, set up redirects from any old pages, and launch once you approve."
              }
            </p>
          </article>
          <article className="rounded-lg border border-border bg-background p-6">
            <h3 className="text-xl font-semibold">{"After launch"}</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              {
                "We watch Search Console and analytics for the first weeks to catch anything unexpected, then move to ongoing maintenance if you choose it."
              }
            </p>
          </article>
        </div>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Redesign Your Website Without Losing Rankings"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "The biggest risk in a redesign is quietly losing the pages that already bring you cases. When URLs change without redirects, or titles and content are dropped, rankings built over years can disappear within weeks. We treat migration as part of the build, not an afterthought."
            }
          </p>
          <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
            <img
              src="/images/services/law-firm-website-development/website-migration-steps.svg"
              alt="Six steps to move a law firm website without losing rankings: inventory every URL, map old URLs to new ones, carry over titles and content that rank, test on a staging site, launch with redirects, and monitor Search Console."
              width="1200"
              height="560"
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              {"Every old URL gets a home on the new site, so rankings and links carry over."}
            </figcaption>
          </figure>
          <ul className="mt-8 space-y-3">
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">
                {"Every existing URL is crawled and listed before we start."}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">
                {"Each old URL gets a permanent redirect to its closest new page."}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">
                {
                  "Titles, descriptions and content that already rank are carried over or improved, not discarded."
                }
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">
                {"Forms, tracking and speed are tested on a staging site before launch."}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">
                {"After launch we check Search Console for errors and fix them quickly."}
              </span>
            </li>
          </ul>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Rebuild or Refresh? Check Your Current Site"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Not every firm needs a new website. Answer eight quick questions to see whether yours needs a rebuild, targeted fixes or just a refresh."
          }
        </p>
        <RebuildOrRefreshCheck />
      </Section>
      <section id="free-plan" className="scroll-mt-24 bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20">
          <HeroLeadForm
            layout="split"
            headingLevel="h2"
            eyebrow="FREE WEBSITE REVIEW"
            heading="Get a free review of your law firm website."
            intro={
              "Tell us about your site and we'll come back within 24 hours with what to keep, what to fix and whether a rebuild makes sense."
            }
            sideContent={
              <>
                <p className="mt-7 text-sm font-bold">{"What we'll look at for your firm:"}</p>
                <ul className="mt-4 space-y-4 text-sm leading-relaxed text-primary-foreground/80">
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"How fast your pages load on a phone, and what's slowing them down"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"How easy it is to call you or send an inquiry from every page"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"Whether each practice area and office has its own page"}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <span className="min-w-0">
                      {"What a rebuild would need to protect so you keep your current rankings"}
                    </span>
                  </li>
                </ul>
                <p className="mt-6 text-xs text-primary-foreground/60">
                  Prefer to talk first?{" "}
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold underline underline-offset-4"
                  >
                    Book a strategy call
                  </a>
                </p>
              </>
            }
          />
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Website Development for Lawyers in Every Practice Area"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "Good lawyer web development starts with the practice area. A personal injury site and an estate planning site serve very different people. One needs urgent, mobile-first contact options; the other needs calm explanations and room for detail. We plan each site around the practice areas you want to grow, and connect it to the SEO work for those areas."
          }
        </p>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          <li>
            <Link
              to="/practice-areas/personal-injury-lawyer-seo"
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"personal injury lawyer SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "family-law-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"family law and divorce SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "criminal-defense-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"criminal defense lawyer SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "estate-planning-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"estate planning lawyer SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas/$slug"
              params={{ slug: "immigration-lawyer-seo" }}
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"immigration lawyer SEO"}
            </Link>
          </li>
          <li>
            <Link
              to="/practice-areas"
              className="block rounded-lg border border-border p-4 font-semibold text-primary transition hover:border-gold hover:text-gold-deep"
            >
              {"all practice areas"}
            </Link>
          </li>
        </ul>
      </Section>
      <div className="border-y border-border bg-secondary/40">
        <Section>
          <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
            {"Website Development Pricing for Law Firms"}
          </h2>
          <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
            {
              "Website development for attorneys is a separate project from our monthly SEO plans, so you only pay for the build you need. We quote each project after a short scoping call, based on the number of pages, the CMS, any integrations such as your CRM or case management system, and whether we're moving an existing site. Maintenance is available as an optional monthly plan after launch."
            }
          </p>
          <ul className="mt-8 space-y-3">
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"Fixed project quote agreed before work starts"}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"Most builds launch in 2 to 4 weeks"}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"You own the website, domain, hosting and files"}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <span className="min-w-0">{"Optional maintenance after launch"}</span>
            </li>
          </ul>
          <div className="mt-8">
            <CTAButton to="#free-plan" variant="gold">
              Get a free website review
            </CTAButton>
          </div>
        </Section>
      </div>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">
          {"Why Firms Choose PageOne as Their Law Firm Website Developers"}
        </h2>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "We only work with law firms, so the sites we build are planned around how people choose a lawyer, not around a generic business template. As a web development agency for law firms that also does SEO, we build every site to rank and convert from launch, and we protect what you already have when we replace an old site. You get a site that's fast on mobile, accessible, easy for your team to update, and fully yours."
          }
        </p>
        <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">
          {
            "If you're comparing attorney website developers, or trying to work out which are the best law firm website developers for your firm, ask each one three things: who owns the site when it's finished, how they protect your rankings during a redesign, and how they'll show you which pages bring in cases. A lawyer website developer should have clear answers to all three, and so should any law firm web development company you consider."
          }
        </p>
      </Section>
      <CaseStudy {...serviceCaseStudy("Web Design for Law Firms")} semanticSubheadings />
      <TrustSignals
        semanticSubheadings
        intro="Every web design for law firms engagement is delivered by the same senior team, on the same guarantees, with reporting tied to signed cases — not clicks."
      />
      <FAQSection
        tone="muted"
        title="Law Firm Website Development: Questions Firms Ask"
        items={FAQS}
        semanticQuestionHeadings
      />
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="mx-auto max-w-4xl font-display text-3xl font-semibold sm:text-4xl md:text-5xl">
            {"Ready for a Website That Brings In Cases?"}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-primary-foreground/80">
            {
              "Get a free review of your current site, with a clear answer on whether to rebuild, fix or refresh it."
            }
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get my free website review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Book a strategy call
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{"Related Services"}</h2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {services
            .filter((item) => item.slug !== "law-firm-website-development")
            .map((item) => (
              <li key={item.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: item.slug }}
                  className="inline-block rounded-full border border-border px-4 py-2 text-sm transition hover:border-gold hover:text-gold-deep"
                >
                  {item.name}
                </Link>
              </li>
            ))}
        </ul>
      </Section>
    </Layout>
  );
}
